import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@tasca/client-sdk';
import type { RemoteStream, VoiceClient } from '@tasca/client-sdk';
import { Icon } from '../ui/Icon.js';

/**
 * Grelha de vídeo adaptativa com modo de foco. Ver docs/05 e a Fase 6 do
 * roadmap.
 *
 * Três coisas acontecem aqui que não se vêem no layout, e são elas que fazem
 * a diferença entre isto escalar e não escalar nesta VPS:
 *
 *  1. **Cada tile pede a camada de simulcast do seu tamanho real.** Um tile
 *     de 160px a receber 720p gasta banda de todos em pixels que ninguém vê.
 *     A decisão tem de ser do cliente: o servidor vê bytes, não layout.
 *
 *  2. **Um tile fora de vista pausa o stream no SERVIDOR.** Não é só não
 *     desenhar: é não receber. É o equivalente em vídeo do que o DTX é no
 *     áudio, e é a optimização de maior retorno de todas.
 *
 *  3. **O separador em segundo plano pausa tudo.** Quem minimiza a janela
 *     continua na conversa a ouvir, não a receber cinco câmaras.
 */

/** Quantas colunas para N vídeos, para a grelha não ficar com órfãos. */
function columnsFor(count: number): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  return 4;
}

const LAYER_LABEL = ['baixa', 'média', 'alta'] as const;

function VideoTile({
  stream,
  voice,
  focused,
  onToggleFocus,
}: {
  stream: RemoteStream;
  voice: VoiceClient;
  focused: boolean;
  onToggleFocus: () => void;
}): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const tileRef = useRef<HTMLElement>(null);
  const status = useStore((s) => s.videoStatus.get(stream.consumerId));
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream.stream;
    void el.play().catch(() => {
      /* bloqueado pelo browser: o join já foi um gesto, raramente acontece */
    });
    return () => {
      el.srcObject = null;
    };
  }, [stream.stream]);

  // ─── Camada pelo tamanho de render ──────────────────────────────────────
  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;

    // O ResizeObserver dispara em rajada ao arrastar uma divisória. O
    // `requestLayerForWidth` do cliente ignora repetições da mesma camada,
    // mas um debounce aqui evita até a conta.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (!width) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => voice.requestLayerForWidth(stream.consumerId, width), 150);
    });
    observer.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [stream.consumerId, voice]);

  // ─── Fora de vista = não receber ────────────────────────────────────────
  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting ?? true;
        voice.setVideoHidden(stream.consumerId, !visible);
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => {
      // Desmontar o tile não deve deixar o stream pausado para sempre: o
      // mesmo consumer pode voltar a ser mostrado noutro sítio da UI.
      voice.setVideoHidden(stream.consumerId, false);
      observer.disconnect();
    };
  }, [stream.consumerId, voice]);

  // ─── Degradação visível ─────────────────────────────────────────────────
  // `spatialLayer: null` significa que o SFU não está a encaminhar camada
  // nenhuma — banda insuficiente até para a mais baixa. Sem este aviso é um
  // rectângulo preto sem explicação.
  useEffect(() => {
    setStalled(status?.spatialLayer === null && stream.source !== 'screen');
  }, [status?.spatialLayer, stream.source]);

  const isScreen = stream.source === 'screen';
  const layer = status?.spatialLayer;
  const score = status?.score;

  return (
    <figure
      ref={tileRef}
      className={`video-tile ${focused ? 'focused' : ''} ${isScreen ? 'screen' : ''}`}
    >
      <video ref={videoRef} autoPlay playsInline muted={false} />

      {stalled && (
        <p className="video-stalled">
          sem banda para esta imagem
          <small>o áudio continua</small>
        </p>
      )}

      <figcaption>
        <span className="video-name">
          {stream.displayName}
          {isScreen && ' · ecrã'}
        </span>

        {/* Qualidade em palavras, não em números: "camada 1" não diz nada a
            quem está só a tentar ver se a chamada está boa. */}
        {!isScreen && layer !== null && layer !== undefined && (
          <span className="video-layer" title={`camada de simulcast ${layer}`}>
            {LAYER_LABEL[layer] ?? layer}
          </span>
        )}
        {score !== null && score !== undefined && score < 7 && (
          <span className="video-score" title={`pontuação RTCP ${score}/10`}>
            instável
          </span>
        )}

        <button
          className="video-focus"
          onClick={onToggleFocus}
          title={focused ? 'Voltar à grelha' : 'Pôr em foco'}
          aria-pressed={focused}
        >
          <Icon name={focused ? 'minimize' : 'maximize'} />
        </button>
      </figcaption>
    </figure>
  );
}

export function VideoGrid({
  streams,
  voice,
}: {
  streams: RemoteStream[];
  voice: VoiceClient;
}): React.ReactElement | null {
  const focusedConsumerId = useStore((s) => s.focusedConsumerId);
  const setFocusedConsumer = useStore((s) => s.setFocusedConsumer);

  // ─── Separador em segundo plano ─────────────────────────────────────────
  useEffect(() => {
    const onVisibility = (): void => {
      const hidden = document.visibilityState === 'hidden';
      for (const stream of streams) voice.setVideoHidden(stream.consumerId, hidden);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [streams, voice]);

  const toggleFocus = useCallback(
    (consumerId: string) => {
      const next = focusedConsumerId === consumerId ? null : consumerId;
      setFocusedConsumer(next);
      // Em foco, aquele vídeo passa a ser o último a ser cortado quando a
      // banda aperta — é o que o utilizador está a dizer ao pô-lo em foco.
      voice.focusVideo(next);
    },
    [focusedConsumerId, setFocusedConsumer, voice],
  );

  if (streams.length === 0) return null;

  const focused = streams.find((s) => s.consumerId === focusedConsumerId) ?? null;
  const others = focused ? streams.filter((s) => s.consumerId !== focused.consumerId) : streams;

  if (focused) {
    return (
      <div className="video-focus-mode">
        <VideoTile
          stream={focused}
          voice={voice}
          focused
          onToggleFocus={() => toggleFocus(focused.consumerId)}
        />
        {others.length > 0 && (
          <div className="video-strip">
            {others.map((s) => (
              <VideoTile
                key={s.consumerId}
                stream={s}
                voice={voice}
                focused={false}
                onToggleFocus={() => toggleFocus(s.consumerId)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="video-grid"
      style={{ ['--cols' as string]: columnsFor(streams.length) }}
    >
      {streams.map((s) => (
        <VideoTile
          key={s.consumerId}
          stream={s}
          voice={voice}
          focused={false}
          onToggleFocus={() => toggleFocus(s.consumerId)}
        />
      ))}
    </div>
  );
}
